import { formatLatLng } from "../../../popup/formattingUtils.js";
import { roundNumber } from "../../../../utils/utils.js";
import { GeoJsonProxy } from '../GeoJsonProxy.js';

export { LeafletGeoJsonProxy };

class LeafletGeoJsonProxy extends GeoJsonProxy {

    constructor(vefLayer) {
        super(vefLayer);

        this.leafletMap = null;
        this.leafletLayer = L.geoJSON(null, {
            pointToLayer: (geoJsonPoint, latlng) => {
                return L.circleMarker(latlng);
            },
            onEachFeature: (feature, layer) => {
                if ((layer instanceof L.Path) || (layer instanceof L.Marker)) {
                    this.bindTooltip_(layer);
                    if (this.vefLayer.autoComputeSize) this.computeSize_(layer);
                    layer.on("click", e => {
                        this.clickedFeatures.push(layer.feature);
                        this.fire("click", {
                            layer: this,
                            feature: layer.feature,
                            mapLayer: layer
                        });
                    });
                }
            },
            style: (feature) => {
                return this.getFeatureStyle(feature);
            }
        });
    }

    getFeatureStyle(feature) {
        if (!("properties" in feature)) feature.properties = {};

        const style = {
            opacity: this.vefLayer.opacity,
            fillOpacity: this.vefLayer.opacity * this.vefLayer.opacityRatio,
            weight: 2
        }

        if ("Color" in feature.properties) {
            style.color = feature.properties.Color;
        } else {
            feature.properties.Color = "";
        }

        return style;
    }

    updateStyle(feature) {
        this.leafletLayer.eachLayer(layer => {
            if (layer.feature) layer.setStyle(this.getFeatureStyle(layer.feature));
        });
    }

    bringToFront() {
        this.leafletLayer.bringToFront();
    }

    bringToBack() {
        this.activeLayer_?.bringToBack();
    }

    addToMap(leafletMap) {
        this.leafletMap = leafletMap;
        this.leafletLayer.addTo(leafletMap);
    }

    removeFromMap() {
        this.leafletLayer.remove();
        this.leafletMap = null;
    }

    clear() {
        this.leafletLayer.clearLayers();
    }

    setData(geoJson) {
        this.clear();
        if(geoJson) this.leafletLayer.addData(geoJson);
    }

    startEditing(feature) {
        this.leafletLayer.eachLayer(layer => {
            if ((layer.feature === feature) && layer.editing) {
                if (layer._tooltip) layer.unbindTooltip();
                layer.editing.enable();

                // allow opening a popup when editing the circlemarker
                if (layer.editing._moveMarker) {
                    layer.editing._moveMarker.on("click", (e) => {
                        this.clickedFeatures.push(layer.feature);
                        this.leafletMap.fire("click", e);
                    });
                }
            }
        });
    }

    stopEditing(feature) {
        this.leafletLayer.eachLayer(layer => {
            if ((layer.feature === feature) && layer.editing) {
                if (layer.editing._moveMarker) layer.editing._moveMarker.off("click");
                layer.editing.disable();
                // workaround for updating layer._bounds after editing
                if ("setLatLngs" in layer) {
                    layer.setLatLngs(layer._latlngs);
                } else if ("setLatLng" in layer) {
                    layer.setLatLng(layer._latlng);
                }
                this.bindTooltip_(layer);
                if (this.vefLayer.autoComputeSize) this.computeSize_(layer);
            }
        });
    }

    getGeoJson() {
        return this.leafletLayer.toGeoJSON();
    }

    setOpacity(opacity) {

    }

    updateTooltips(feature, label) {
        this.leafletLayer.eachLayer(layer => {
            if (layer.feature === feature) {
                this.bindTooltip_(layer);
            }
        });
    }

    getBounds() {
        const bounds = this.leafletLayer.getBounds();
        return {
            min: {
                x: bounds.getWest(),
                y: bounds.getSouth()
            },
            max: {
                x: bounds.getEast(),
                y: bounds.getNorth()
            },
            crs: "CRS:84"
        }
    }

    /**
     * @private
     */
    bindTooltip_(layer) {
        if (layer._tooltip) layer.unbindTooltip();
        if (("Label" in layer.feature.properties) && (typeof layer.feature.properties.Label == "string") && (layer.feature.properties.Label.length > 0)) {
            layer.bindTooltip(layer.feature.properties.Label, {
                permanent: true,
                opacity: 1,
                className: "leaflet-tooltip-label"
            });
        }
    }

    /**
     * static method for computing the size of each feature and add
     * it to the feature properties
     * @param {L.Layer} layer
     * @private
     */
    computeSize_(layer) {
        if (!("feature" in layer)) return;

        if (!(layer instanceof L.CircleMarker)) {
            // bounding box
            const bbox = layer.getBounds();
            const nw = bbox.getNorthWest();
            const se = bbox.getSouthEast();

            layer.feature.properties["North West"] = formatLatLng(nw.lat, nw.lng, "degreeminutes");
            layer.feature.properties["South East"] = formatLatLng(se.lat, se.lng, "degreeminutes");
        } else {
            // circle merker position
            const lat = layer._latlng.lat;
            const lng = layer._latlng.lng;
            layer.feature.properties["Coordinates"] = formatLatLng(lat, lng, "degreeminutes");
        }

        if ((layer instanceof L.Polyline) && !(layer instanceof L.Polygon)) {
            // measure distance
            let distance = 0;
            const latLngs = layer.getLatLngs();
            for (let i = 1; i < latLngs.length; ++i) {
                // check nesting for MultiPolyline
                if (Array.isArray(latLngs[i])) {
                    const child = latLngs[i];
                    for (let j = 1; j < child.length; ++j) {
                        distance += child[j - 1].distanceTo(child[j]);
                    }
                } else {
                    distance += latLngs[i - 1].distanceTo(latLngs[i]);
                }
            }
            layer.feature.properties["Distance"] = roundNumber(distance / 1000, 3) + " km";
        } else if (layer instanceof L.Polygon) {
            // measure area
            const geodesicArea = L.GeometryUtil.geodesicArea(layer.getLatLngs()[0]);
            let readableArea = L.GeometryUtil.readableArea(geodesicArea, true, 3);
            if (readableArea.endsWith("ha")) {
                readableArea = roundNumber(parseFloat(readableArea) / 100, 3) + " km²";
            }
            layer.feature.properties["Geodesic Area"] = readableArea;
        }
    }

}